<template>
  <div>
    <global-confirm-box ref="confirmBox"></global-confirm-box>

    <page-header
      :title="$t('group_list')"
      :subtitle="$t('manage_meal_categories')"
      icon="mdi-food-variant"
      icon-color="primary"
    >
      <template #actions v-if="!device.isMobile">
        <primary-button :disabled="loading.categories" @click="openCreateDialog">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add_group") }}
        </primary-button>
      </template>
    </page-header>

    <!-- Desktop View -->
    <template v-if="!device.isMobile">
      <page-toolbar
        :search.sync="search"
        searchable
        :search-placeholder="$t('search_categories_placeholder')"
      >
      </page-toolbar>

      <div class="px-6 mt-2">
        <v-data-table
          :headers="headers"
          :items="categories"
          :search="search"
          :loading="loading.categories"
          class="striped-table ordera-box-shadow"
          mobile-breakpoint="0"
          dense
          show-expand
          :loading-text="$t('loading')"
        >
          <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
            <template v-if="item.children && item.children.length">
              <v-btn
                color="primary"
                dark
                small
                rounded
                @click="expand(true)"
                v-if="!isExpanded"
              >
                {{ $t("show") }}
              </v-btn>
              <v-btn color="primary" dark small rounded @click="expand(false)" v-else>
                {{ $t("hide") }}
              </v-btn>
            </template>
          </template>
          <template v-slot:header.can_reserve>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <span v-on="on">{{ $t("reserve_group") }}</span>
              </template>
              <span>{{ $t("spare_meal_group_hint") }}</span>
            </v-tooltip>
          </template>

          <template v-slot:expanded-item="{ item }">
            <template v-if="item.children">
              <td :colspan="headers.length" class="px-0">
                <v-data-table
                  dense
                  :headers="headers"
                  class="ordera-box-shadow striped-table"
                  :items="item.children || []"
                  hide-default-footer
                  hide-default-header
                >
                  <template v-slot:item.image="{ item }">
                    <v-progress-circular
                      v-if="loading.uploading[item.id]"
                      indeterminate
                      color="primary"
                      size="20"
                    />
                    <template v-else-if="item.image">
                      <v-badge
                        overlap
                        icon="mdi-close"
                        color="error"
                        bordered
                        @click.native.stop="removeImage(item)"
                        style="cursor: pointer"
                      >
                        <v-img
                          height="40"
                          width="40"
                          :src="galleryUrl(item.image.name)"
                        />
                      </v-badge>
                    </template>
                    <template v-else>
                      <label :for="'upload-' + item.id" style="cursor: pointer">
                        <v-icon large>mdi-image-plus</v-icon>
                      </label>
                      <input
                        type="file"
                        :id="'upload-' + item.id"
                        @change="uploadImage(item, $event)"
                        :disabled="loading.uploading[item.id]"
                        class="d-none"
                      />
                    </template>
                  </template>
                  <template v-slot:item.prilog="{ item }">
                    <div class="text-right">
                      <v-checkbox
                        :disabled="Boolean(item.main_group) || loading.updating[item.id]"
                        v-model="item.prilog"
                        @change="setAttachment(item)"
                        :loading="loading.updating[item.id]"
                      ></v-checkbox>
                    </div>
                  </template>
                  <template v-slot:item.main_group="{ item }">
                    <div class="text-right">
                      <v-checkbox
                        :disabled="Boolean(item.prilog) || loading.updating[item.id]"
                        v-model="item.main_group"
                        @change="setMainGroup(item)"
                        :loading="loading.updating[item.id]"
                      ></v-checkbox>
                    </div>
                  </template>
                  <template v-slot:item.exclude_from_package_count="{ item }">
                    <div
                      class="text-right"
                      :title="item.prilog ? $t('addons_never_counted') : ''"
                    >
                      <v-checkbox
                        :input-value="
                          Boolean(item.prilog) || Boolean(item.exclude_from_package_count)
                        "
                        :disabled="Boolean(item.prilog) || loading.updating[item.id]"
                        @change="setExcludeFromPackageCount(item, $event)"
                        :loading="loading.updating[item.id]"
                      ></v-checkbox>
                    </div>
                  </template>
                  <template v-slot:item.name="{ item }">
                    {{ toLatin(item.name) }}
                  </template>
                  <template v-slot:item.actions="{ item }">
                    <edit-button
                      class="mr-2"
                      @click="editCategory(item)"
                      :disabled="loading.deleting || loading.updating[item.id]"
                    ></edit-button>
                    <remove-button
                      @click="deleteCategory(item)"
                      :loading="loading.deleting"
                      :disabled="loading.deleting || loading.updating[item.id]"
                    ></remove-button>
                  </template>
                </v-data-table>
              </td>
            </template>
          </template>
          <template v-slot:item.image="{ item }">
            <v-progress-circular
              v-if="loading.uploading[item.id]"
              indeterminate
              color="primary"
              size="20"
            />
            <template v-else-if="item.image">
              <v-badge
                overlap
                icon="mdi-close"
                color="error"
                bordered
                @click.native.stop="removeImage(item)"
                style="cursor: pointer"
              >
                <v-img height="40" width="40" :src="galleryUrl(item.image.name)" />
              </v-badge>
            </template>
            <template v-else>
              <label :for="'upload-' + item.id" style="cursor: pointer">
                <v-icon large>mdi-image-plus</v-icon>
              </label>
              <input
                type="file"
                :id="'upload-' + item.id"
                @change="uploadImage(item, $event)"
                :disabled="loading.uploading[item.id]"
                class="d-none"
              />
            </template>
          </template>
          <template v-slot:item.can_reserve="{ item }" v-if="canRestaurantReserve">
            <v-switch
              v-if="item.reserved || noReservedCategory"
              v-model="item.reserved"
              hide-details
              :true-value="1"
              :false-value="0"
              @change="updateCanReserve(item)"
            ></v-switch>
          </template>
          <template v-slot:item.prilog="{ item }">
            <div class="text-right">
              <v-checkbox
                :disabled="Boolean(item.main_group) || loading.updating[item.id]"
                v-model="item.prilog"
                @change="setAttachment(item)"
                :loading="loading.updating[item.id]"
              ></v-checkbox>
            </div>
          </template>
          <template v-slot:item.main_group="{ item }">
            <div class="text-right">
              <v-checkbox
                :disabled="Boolean(item.prilog) || loading.updating[item.id]"
                v-model="item.main_group"
                @change="setMainGroup(item)"
                :loading="loading.updating[item.id]"
              ></v-checkbox>
            </div>
          </template>
          <template v-slot:item.exclude_from_package_count="{ item }">
            <div class="text-right" :title="item.prilog ? $t('addons_never_counted') : ''">
              <v-checkbox
                :input-value="
                  Boolean(item.prilog) || Boolean(item.exclude_from_package_count)
                "
                :disabled="Boolean(item.prilog) || loading.updating[item.id]"
                @change="setExcludeFromPackageCount(item, $event)"
                :loading="loading.updating[item.id]"
              ></v-checkbox>
            </div>
          </template>
          <template v-slot:item.name="{ item }">
            {{ toLatin(item.name) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-row no-gutters justify="end">
              <edit-button
                class="mr-2"
                @click="editCategory(item)"
                :disabled="loading.deleting || loading.updating[item.id]"
              />
              <remove-button
                @click="deleteCategory(item)"
                :loading="loading.deleting"
                :disabled="loading.deleting || loading.updating[item.id]"
              />
            </v-row>
          </template>
        </v-data-table>
      </div>
    </template>

    <!-- Mobile View -->
    <template v-else>
      <div class="px-3 mt-4">
        <mobile-data-cards
          :headers="mobileHeaders"
          :items="categories"
          :loading="loading.categories"
          item-key="id"
          show-filters
          :show-row-number="false"
          :fields-per-row="null"
          :bold-fields="['name']"
        >
          <template v-slot:item.image="{ item }">
            <v-progress-circular
              v-if="loading.uploading[item.id]"
              indeterminate
              color="primary"
              size="20"
            />
            <template v-else-if="item.image">
              <v-img height="40" width="40" :src="galleryUrl(item.image.name)" />
            </template>
            <template v-else>
              <v-icon>mdi-image</v-icon>
            </template>
          </template>
          <template v-slot:item.name="{ item }">
            {{ toLatin(item.name) }}
          </template>
          <template v-slot:item.prilog="{ item }">
            <v-checkbox
              :disabled="Boolean(item.main_group) || loading.updating[item.id]"
              v-model="item.prilog"
              @change="setAttachment(item)"
              :loading="loading.updating[item.id]"
              hide-details
            ></v-checkbox>
          </template>
          <template v-slot:item.main_group="{ item }">
            <v-checkbox
              :disabled="Boolean(item.prilog) || loading.updating[item.id]"
              v-model="item.main_group"
              @change="setMainGroup(item)"
              :loading="loading.updating[item.id]"
              hide-details
            ></v-checkbox>
          </template>
          <template v-slot:item.exclude_from_package_count="{ item }">
            <v-checkbox
              :input-value="Boolean(item.prilog) || Boolean(item.exclude_from_package_count)"
              :disabled="Boolean(item.prilog) || loading.updating[item.id]"
              @change="setExcludeFromPackageCount(item, $event)"
              :loading="loading.updating[item.id]"
              hide-details
            ></v-checkbox>
          </template>
          <template v-slot:item.actions="{ item }">
            <edit-button
              class="mr-2"
              @click="editCategory(item)"
              :disabled="loading.deleting || loading.updating[item.id]"
            ></edit-button>
            <remove-button
              @click="deleteCategory(item)"
              :loading="loading.deleting"
              :disabled="loading.deleting || loading.updating[item.id]"
            ></remove-button>
          </template>
        </mobile-data-cards>
      </div>

      <div class="fixed-bottom-button">
        <v-btn
          color="primary"
          block
          :disabled="loading.categories"
          @click="openCreateDialog"
        >
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add_group") }}
        </v-btn>
      </div>
    </template>

    <CategoryDialog
      v-model="dialog"
      :categories="categories"
      :category="editedCategory"
      :is-edit="!!editedCategory.id"
      @category-created="addCategoryToList"
      @category-updated="updateCategoryInList"
      @close="editedCategory = {}"
    />
  </div>
</template>

<script>
import { defaultCategory } from "@/models/category";
import categoryService from "@/services/category";
import CategoryDialog from "@/components/categories/dialogs/CategoryDialog.vue";
import PageHeader from "@/components/global/PageHeader.vue";
import PageToolbar from "@/components/global/PageToolbar.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasSnackbar from "@/mixins/hasSnackbar";
import TextField from '../../components/global/fields/text-field.vue';

export default {
  mixins: [hasSnackbar],
  components: {
    CategoryDialog,
    PageHeader,
    PageToolbar,
    MobileDataCards,
    TextField
  },

  data: () => ({
    dialog: false,
    search: "",
    categories: [],
    editedCategory: {},
    loading: {
      categories: false,
      deleting: false,
      uploading: {},
      updating: {},
    },
  }),

  computed: {
    noReservedCategory() {
      return !(this.categories || []).some((cat) => cat.reserved);
    },
    canRestaurantReserve() {
      return this.$store.state.restaurant?.settings?.can_reserve;
    },
    headers() {
      const headers = [
        { text: "ID", align: "start", value: "id", width: "100px" },
        { text: this.$t("image"), align: "start", value: "image" },
        { text: this.$t("title"), align: "start", value: "name" },
        { text: this.$t("group_addon"), align: "start", value: "prilog" },
        { text: this.$t("main_group"), align: "start", value: "main_group" },
        {
          text: this.$t("exclude_from_package_count"),
          align: "start",
          value: "exclude_from_package_count",
        },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          align: "right",
          width: "120px",
        },
        { text: "", value: "data-table-expand" },
      ];
      if (this.canRestaurantReserve) {
        headers.splice(5, 0, { text: this.$t("reserve_group"), value: "can_reserve" });
      }
      return headers;
    },
    mobileHeaders() {
      return [
        { text: this.$t("title"), value: "name", cols: 6 },
        { text: this.$t("image"), value: "image", cols: 6 },
        { text: this.$t("group_addon"), value: "prilog", cols: 3 },
        { text: this.$t("main_group"), value: "main_group", cols: 5 },
        {
          text: this.$t("exclude_from_package_count"),
          value: "exclude_from_package_count",
          cols: 6,
        },
        { text: this.$t("actions"), value: "actions", cols: 4 },
      ];
    },
  },

  created() {
    this.loadCategories();
  },

  methods: {
    updateCanReserve(category) {
      const prev = category.reserved;
      categoryService
        .updateCategory(category.id, category)
        .then(() => {
          this.showSuccessSnackbar(this.$t("reserve_group_updated_successfully"));
        })
        .catch((error) => {
          category.reserved = prev ? 0 : 1;
          this.showCategoryError(error, "error_updating_reserve_group");
          console.error("Error updating category:", error);
        });
    },

    // 403: restoran moze da menja samo svoje grupe; ostalo je opsta greska
    showCategoryError(error, fallbackKey) {
      const message =
        error?.response?.status === 403
          ? this.$t("category_forbidden")
          : this.$t(fallbackKey);
      this.showErrorSnackbar(message);
    },

    setLoadingState(type, key, state) {
      if (state) {
        this.$set(this.loading[type], key, true);
      } else {
        this.$delete(this.loading[type], key);
      }
    },

    findCategoryById(categoryId, categories = this.categories) {
      for (let category of categories) {
        if (category.id === categoryId) return category;
        if (category.children) {
          const found = this.findCategoryById(categoryId, category.children);
          if (found) return found;
        }
      }
      return null;
    },

    removeImage(item) {
      this.setLoadingState("uploading", item.id, true);
      categoryService
        .deleteCategoryImage(item.id)
        .then((res) => {
          if (res.data) {
            this.updateCategoryProperty(item.id, { image_id: null, image: null });
            this.showSuccessSnackbar(this.$t("image_deleted_successfully"));
          }
        })
        .catch((error) => {
          this.showCategoryError(error, "error_deleting_image");
        })
        .finally(() => {
          this.setLoadingState("uploading", item.id, false);
        });
    },

    uploadImage(item, event) {
      if (!event.target.files[0]) return;
      this.setLoadingState("uploading", item.id, true);

      const formData = new FormData();
      formData.append("image", event.target.files[0]);
      formData.append("category_id", item.id);

      categoryService
        .uploadCategoryImage(formData)
        .then((res) => {
          if (res.data) {
            this.updateCategoryProperty(item.id, {
              image_id: res.data.id,
              image: res.data,
            });
            this.showSuccessSnackbar(this.$t("image_uploaded_successfully"));
          }
        })
        .catch((error) => {
          this.showCategoryError(error, "error_uploading_image");
        })
        .finally(() => {
          this.setLoadingState("uploading", item.id, false);
          event.target.value = "";
        });
    },

    updateCategoryProperty(categoryId, updates) {
      const category = this.findCategoryById(categoryId);
      if (category) Object.assign(category, updates);
    },

    setMainGroup(category) {
      this.setLoadingState("updating", category.id, true);
      categoryService
        .setMainGroupMeal(category)
        .then(() => {
          this.showSuccessSnackbar(this.$t("main_group_updated_successfully"));
        })
        .catch((error) => {
          category.main_group = !category.main_group;
          this.showCategoryError(error, "error_updating_main_group");
        })
        .finally(() => {
          this.setLoadingState("updating", category.id, false);
        });
    },

    setAttachment(category) {
      this.setLoadingState("updating", category.id, true);
      categoryService
        .setAttachment(category)
        .then(() => {
          this.showSuccessSnackbar(this.$t("attachment_updated_successfully"));
        })
        .catch((error) => {
          category.prilog = !category.prilog;
          this.showCategoryError(error, "error_updating_attachment");
        })
        .finally(() => {
          this.setLoadingState("updating", category.id, false);
        });
    },

    setExcludeFromPackageCount(category, checked) {
      const value = checked ? 1 : 0;
      const previous = category.exclude_from_package_count;
      this.$set(category, "exclude_from_package_count", value);
      this.setLoadingState("updating", category.id, true);
      categoryService
        .setExcludeFromPackageCount({
          id: category.id,
          exclude_from_package_count: value,
        })
        .then(() => {
          this.showSuccessSnackbar(
            this.$t("exclude_from_package_count_updated_successfully")
          );
        })
        .catch((error) => {
          this.$set(category, "exclude_from_package_count", previous);
          this.showCategoryError(error, "error_updating_exclude_from_package_count");
        })
        .finally(() => {
          this.setLoadingState("updating", category.id, false);
        });
    },

    loadCategories() {
      this.loading.categories = true;
      categoryService
        .getCategories()
        .then((res) => {
          this.categories = res.data;
        })
        .finally(() => {
          this.loading.categories = false;
        });
    },

    editCategory(item) {
      this.editedCategory = { ...item };
      this.dialog = true;
    },

    deleteCategory(item) {
      this.$refs.confirmBox
        .open(this.$t("delete_category"), this.$t("delete_category_confirmation"), {
          color: "error",
        })
        .then(() => {
          this.loading.deleting = true;
          categoryService
            .deleteCategory(item.id)
            .then(() => {
              this.removeCategoryFromList(item);
              this.showSuccessSnackbar(this.$t("category_deleted_successfully"));
            })
            .catch((error) => {
              this.showCategoryError(error, "error_deleting_category");
            })
            .finally(() => {
              this.loading.deleting = false;
            });
        })
        .catch((err) => console.warn(err));
    },

    removeCategoryFromList(item) {
      if (item.parent_id) {
        const parent = this.categories.find((cat) => cat.id === item.parent_id);
        if (parent?.children) {
          const idx = parent.children.findIndex((child) => child.id === item.id);
          if (idx > -1) parent.children.splice(idx, 1);
        }
      } else {
        const idx = this.categories.findIndex((cat) => cat.id === item.id);
        if (idx > -1) this.categories.splice(idx, 1);
      }
    },

    openCreateDialog() {
      this.editedCategory = { ...defaultCategory };
      this.dialog = true;
    },

    addCategoryToList(category) {
      if (category.parent_id) {
        const parent = this.categories.find((cat) => cat.id === category.parent_id);
        if (parent) {
          if (!parent.children) this.$set(parent, "children", []);
          parent.children.push(category);
        }
      } else {
        this.categories.push(category);
      }
    },

    updateCategoryInList(category) {
      if (category.parent_id) {
        const parent = this.categories.find((cat) => cat.id === category.parent_id);
        if (parent?.children) {
          const idx = parent.children.findIndex((child) => child.id === category.id);
          if (idx > -1) Object.assign(parent.children[idx], category);
        }
      } else {
        const idx = this.categories.findIndex((cat) => cat.id === category.id);
        if (idx > -1) Object.assign(this.categories[idx], category);
      }
    },
  },
};
</script>
<style scoped>
.fixed-bottom-button {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: white;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;
}
</style>
